import './styles/index.css';
import { WirespanApp } from './app';
import { dayNumber } from './game/daily';
import { EVENTS_URL } from './state/endpoints';
import { initPerf } from './state/perf';
import { envelopeOf, loadIdentity } from './state/identity';
import { loadLocale, loadUsage } from './state/progress';
import { createStopwatch, initTelemetry, track } from './state/telemetry';
import { must } from './ui/dom';
import { detectLocale, locale, preferredLanguage, setLocale } from './ui/locale';
import { applyStaticStrings, str } from './ui/strings';
import { toast } from './ui/toast';
import { registerServiceWorker } from './pwa';

// Open in the browser's own language, before a word is painted: a stored
// choice overrules it inside app.start(), which is soon enough that nothing
// has been read yet, and this way a Spanish browser never flashes English.
setLocale(detectLocale());
// The dictionary owns the copy; the markup's text is a pre-JS fallback.
applyStaticStrings();

const board = must<SVGSVGElement>('board');
const app = new WirespanApp(board);

/**
 * Arm the usage stream, if the player has left it on.
 *
 * Identity is loaded before the envelope is snapshotted, so a first-ever
 * session already reads as its first visit rather than as its zeroth. Nothing
 * here can stop the game starting: the whole stream is off the path of play
 * (GDD Pillar 5), and a failure to arm is a failure to measure, not to play.
 */
async function armTelemetry(localeSource: 'chosen' | 'browser'): Promise<void> {
  if (!import.meta.env.PROD) return;

  const usage = await loadUsage();
  // The id is minted either way and never sent while the switch is off.
  const identity = await loadIdentity(dayNumber(), {
    referrer: document.referrer,
    here: location.href,
    search: location.search,
  });

  // Armed either way, and silent when the switch is off. Arming only when it
  // is on would leave the switch inert for the rest of the session, and a
  // player who turned it back on would get nothing until they reloaded — a
  // worse promise than the one the settings row makes.
  initTelemetry({
    url: EVENTS_URL,
    enabled: usage,
    clock: createStopwatch(),
    context: {
      ...envelopeOf(identity),
      build: __BUILD_ID__,
    },
  });

  track('app_open', {
    standalone: globalThis.matchMedia?.('(display-mode: standalone)').matches ?? false,
    serviceWorker: Boolean(navigator.serviceWorker?.controller),
    storageOk: storageWorks(),
    // Which language the game is actually being played in, whether that was
    // chosen or inherited, and which language this browser asks for first —
    // together, what is worth translating next (GDD §12).
    locale: locale(),
    localeSource,
    prefLang: preferredLanguage(),
  });

  armFirstInput();

  // How the game actually runs on this device (GDD §12). Armed after the
  // stream, because it reports through it; passive throughout, because a
  // sampling loop that exists to time frames is itself a reason a page can
  // never go idle.
  initPerf();

  // Capped by the batch size like anything else, and the message has any
  // address stripped from it server-side before it is stored.
  //
  // Where the failing script came from is the difference between a bug and a
  // passer-by. The bundle is same-origin and served with `crossorigin`, so a
  // fault in this game's own code arrives with a filename and a real message;
  // an extension or an injected third-party script is opaque by design and
  // arrives as a bare "Script error." with no filename at all. Counted
  // together the second kind buries the first — on 2026-08-24 nine of ten
  // rows were opaque, from four devices, and told us nothing.
  const sourceOf = (filename: string): 'app' | 'foreign' | 'opaque' => {
    if (!filename) return 'opaque';
    try {
      return new URL(filename).origin === window.location.origin ? 'app' : 'foreign';
    } catch {
      // A filename that will not parse is not ours; ours are absolute URLs.
      return 'foreign';
    }
  };

  // Three of each source, rather than three in total: a noisy extension must
  // not be able to spend the budget before this game's own bug is reached.
  const errors = new Map<string, number>();
  const report = (
    where: 'window' | 'promise',
    errorSource: 'app' | 'foreign' | 'opaque' | 'unknown',
    message: string,
  ) => {
    const seen = errors.get(errorSource) ?? 0;
    if (seen >= 3) return;
    errors.set(errorSource, seen + 1);
    track('client_error', { where, errorSource, message: message.slice(0, 200) });
  };
  window.addEventListener('error', (e) => report('window', sourceOf(e.filename), e.message));
  // A rejection carries no filename, and the only other place its origin is
  // written is a stack — which is a path, so it is not asked for. The message
  // is readable either way, which is the part worth having.
  window.addEventListener('unhandledrejection', (e) =>
    report('promise', 'unknown', String(e.reason)),
  );
}

/**
 * Report the session's first trusted input, once.
 *
 * A page load is not a player (GDD §12): a crawler that executes the game is
 * dealt a grid like anyone else, so arrival proves nothing. The one fact that
 * separates a person from a page-load is that a hand touched the page, and
 * `isTrusted` is the browser's own word for it. Only the kind of input is
 * sent — never what was pressed, or where.
 */
function armFirstInput(): void {
  const kinds = [
    ['pointerdown', 'pointer'],
    ['pointermove', 'move'],
    ['keydown', 'key'],
    ['wheel', 'wheel'],
  ] as const;
  const detach: Array<() => void> = [];
  for (const [name, kind] of kinds) {
    const handler = (e: Event): void => {
      if (!e.isTrusted) return;
      for (const off of detach) off();
      track('first_input', { inputKind: kind });
    };
    window.addEventListener(name, handler, { capture: true, passive: true });
    detach.push(() => window.removeEventListener(name, handler, { capture: true }));
  }
}

/** Whether this browser will actually keep a save. */
function storageWorks(): boolean {
  try {
    const probe = '__wirespan_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Settle the language before a word is measured or a grid is dealt.
 *
 * The page already opened in the browser's language, synchronously, so this
 * only has to overrule it with a stored choice — and say which of the two the
 * player ended up with, because "chose Spanish" and "was given Spanish" are
 * different facts about a translation.
 */
async function applyLocale(): Promise<'chosen' | 'browser'> {
  const saved = await loadLocale();
  if (!saved) return 'browser';
  if (saved !== locale()) {
    setLocale(saved);
    applyStaticStrings();
  }
  return 'chosen';
}

async function boot(): Promise<void> {
  const localeSource = await applyLocale();
  try {
    await armTelemetry(localeSource);
  } catch (err) {
    console.error('Usage stream failed to arm', err);
  }
  await app.start();
}

void boot().catch((err: unknown) => {
  console.error('Wirespan failed to start', err);
  toast(() => str().gridFailed);
});

registerServiceWorker(() => app.safeToReload());
